import React, { useEffect, useState } from "react";
import { Drawer } from "@material-ui/core";
import ProductFilterList from "./ProductFilterList";
import Button from "@material-ui/core/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  applyFilters,
  setFetchCategory,
  setLoading,
} from "../../../store/actions/productFilter";
import instance from "../../../store/actions/instance";
import { isEqual } from "./helper";
import { saveSearch } from "../../../store/actions/store";
import ProductHeader from "../ProductHeader";
import CircularProgress from "@material-ui/core/CircularProgress";
import "./ProductFilter.css";

const ProductFilter = ({ filterDrawerOpen, handleFilterDrawerToggle }) => {
  //REDUX
  const dispatch = useDispatch();
  const themeColor = useSelector((state) => state.store.store.settings?.dark);
  const minPrice = useSelector(
    (state) => state.productFilter?.allProductPriceRange?.[0]
  );
  const maxPrice = useSelector(
    (state) => state.productFilter?.allProductPriceRange?.[1]
  );
  const selectedFilters = useSelector(
    (state) => state.productFilter.filtersSelected
  );
  const nutritionalFactMaxMin = useSelector(
    (state) => state.productFilter?.allProductNutritionFact
  );
  const fetchFiltersLoading = useSelector(
    (state) => state.productFilter?.fetchLoading
  );

  const channel = useSelector((state) => state.promise.src);
  //FILTERS TYPES
  const sortByFilter = useSelector((state) => state.productFilter.sortBy);
  const priceRange = useSelector((state) => state.productFilter.priceRange);
  const branch = useSelector((state) => state.branch);
  const categoriesFilter = useSelector(
    (state) => state.productFilter.selectedCategories
  );
  const availableProductsFilter = useSelector(
    (state) => state.productFilter.availableProducts
  );
  const nutritionalFactFilter = useSelector(
    (state) => state.productFilter.nutritionalFact
  );

  const settings = useSelector((state) => state.store.store.settings);
  const language = useSelector((state) => state.store.language);

  //COMPONENT STATE
  const [filtersToApply, setFiltersToApply] = useState(selectedFilters);
  const [applyError, setApplyError] = useState(false);
  const [applying, setApplying] = useState(false);
  const [sortOption, setSortOption] = useState(sortByFilter);
  const [priceRangeToApply, setPriceRangeToApply] = useState(
    priceRange.range || [minPrice, maxPrice]
  );
  const [categoriesToApply, setCategoriesToApply] = useState(categoriesFilter);
  const [availableProducts, setAvailableProducts] = useState(
    availableProductsFilter
  );

  const checkIfNutritionalIsNull = () => {
    return (
      nutritionalFactFilter?.calories === null &&
      nutritionalFactFilter?.fats === null &&
      nutritionalFactFilter?.protein === null &&
      nutritionalFactFilter?.carbs === null &&
      nutritionalFactFilter?.dietaryRestrictions.length === 0
    );
  };

  const [nutritionalFact, setNutritionalFact] = useState(
    checkIfNutritionalIsNull()
      ? {
          ...nutritionalFactMaxMin,
          dietaryRestrictions: [],
        }
      : nutritionalFactFilter
  );

  const checkIfNutritionalIsMinMax = () => {
    return (
      isEqual(nutritionalFact.calories, nutritionalFactMaxMin?.calories) &&
      isEqual(nutritionalFact.fats, nutritionalFactMaxMin?.fats) &&
      isEqual(nutritionalFact.protein, nutritionalFactMaxMin?.protein) &&
      isEqual(nutritionalFact.carbs, nutritionalFactMaxMin?.carbs) &&
      nutritionalFact.dietaryRestrictions.length === 0
    );
  };

  //COMPONENT FUNCTIONS
  const handleApplyFilters = () => {
    if (
      priceRangeToApply[0] !== minPrice ||
      priceRangeToApply[1] !== maxPrice
    ) {
      dispatch(
        applyFilters(
          filtersToApply,
          sortOption,
          priceRangeToApply,
          categoriesToApply,
          availableProducts,
          checkIfNutritionalIsMinMax() ? null : nutritionalFact
        )
      );
    } else {
      dispatch(
        applyFilters(
          filtersToApply,
          sortOption,
          null,
          categoriesToApply,
          availableProducts,
          checkIfNutritionalIsMinMax() ? null : nutritionalFact
        )
      );
    }
    handleFilterDrawerToggle(false);
  };

  const filtersToApplyIds = filtersToApply.map((filter) => filter.value);
  const checkIfBranchBased =
    settings.is_branch_based_ordering && (branch.branch || branch.bdc);

  //FETCH FUNCTION FOR GRID AND SCROLL VIEW ONLY
  const fetchFilteredCategories = async () => {
    try {
      dispatch(setLoading(true));
      const res = checkIfBranchBased
        ? await instance.post("/filter_products/", {
            categories: categoriesToApply,
            filter_options: filtersToApplyIds,
            priceRange:
              priceRangeToApply[0] === minPrice &&
              priceRangeToApply[1] === maxPrice
                ? null
                : priceRangeToApply,
            sortBy: sortOption.value,
            type: branch.isPickup ? "pickup" : "delivery",
            branch_id: branch.branch,
            bdc_id: !branch.isPickup ? branch.bdc : branch.branch,
            nutritionalFact: checkIfNutritionalIsMinMax()
              ? null
              : nutritionalFact,
            channel: channel,
          })
        : await instance.post("/filter_products/", {
            categories: categoriesToApply,
            filter_options: filtersToApplyIds,
            priceRange:
              priceRangeToApply[0] === minPrice &&
              priceRangeToApply[1] === maxPrice
                ? null
                : priceRangeToApply,
            sortBy: sortOption.value,
            nutritionalFact: checkIfNutritionalIsMinMax()
              ? null
              : nutritionalFact,
            channel: channel,
          });

      if (res.data.success === true) {
        dispatch(setFetchCategory(res.data.categories));
        let products = [];
        res.data.categories.forEach(
          (cat) => (products = products.concat(cat.products))
        );

        dispatch(saveSearch(products));
        return true;
      }
      return false;
    } catch (err) {
      return false;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCloseFilterDrawer = () => {
    const isFilterChanged =
      selectedFilters.length === 0 ||
      sortByFilter.value === "" ||
      priceRangeToApply[0] !== minPrice ||
      priceRangeToApply[1] !== maxPrice ||
      availableProducts === false ||
      !isEqual(nutritionalFact, {
        ...nutritionalFactMaxMin,
        dietaryRestrictions: [],
      });
    if (isFilterChanged) {
      setFiltersToApply(selectedFilters);
      setSortOption(sortByFilter);
      setPriceRangeToApply([priceRangeToApply[0], priceRangeToApply[1]]);
      setCategoriesToApply(categoriesFilter);
      setAvailableProducts(availableProductsFilter);
      setNutritionalFact(nutritionalFact);
    }

    handleFilterDrawerToggle(false);
  };

  const handleButtonDisable = () => {
    return (
      isEqual(filtersToApply, selectedFilters) &&
      isEqual(categoriesToApply, categoriesFilter) &&
      sortOption.value === sortByFilter.value &&
      (priceRange.range === null
        ? priceRangeToApply[0] === minPrice && priceRangeToApply[1] === maxPrice
        : priceRangeToApply[0] === priceRange.range[0] &&
          priceRangeToApply[1] === priceRange.range[1]) &&
      availableProducts === availableProductsFilter &&
      (checkIfNutritionalIsNull()
        ? isEqual(
            {
              ...nutritionalFactMaxMin,
              dietaryRestrictions: [],
            },
            nutritionalFact
          )
        : isEqual(nutritionalFact, nutritionalFactFilter))
    );
  };

  const handleClearFilters = () => {
    setFiltersToApply([]);
    setSortOption({ filterName: "Sort By", filterOptionName: "", value: "" });
    setPriceRangeToApply([minPrice, maxPrice]);
    setCategoriesToApply([]);
    setAvailableProducts(false);
    setNutritionalFact({
      ...nutritionalFactMaxMin,
      dietaryRestrictions: [],
    });
  };

  const handleFilter = async () => {
    if (applying) return;
    setApplying(true);
    setApplyError(false);
    if (
      filtersToApply?.length > 0 ||
      settings.store_layout === "grid" ||
      settings.store_layout === "grid2" ||
      settings.store_layout === "scroll"
    ) {
      const success = await fetchFilteredCategories();
      if (!success) {
        setApplyError(true);
        setApplying(false);
        return;
      }
    }
    handleApplyFilters();
    setApplying(false);
  };

  useEffect(() => {
    setFiltersToApply(selectedFilters);
    setSortOption(sortByFilter);
    if (priceRange.range !== null) {
      setPriceRangeToApply(priceRange.range);
    } else {
      setPriceRangeToApply([minPrice, maxPrice]);
    }
    setCategoriesToApply(categoriesFilter);
    setAvailableProducts(availableProductsFilter);
    if (!checkIfNutritionalIsNull()) {
      setNutritionalFact(nutritionalFactFilter);
    } else {
      setNutritionalFact({
        ...nutritionalFactMaxMin,
        dietaryRestrictions: [],
      });
    }
  }, [
    selectedFilters,
    sortByFilter,
    priceRange,
    categoriesFilter,
    availableProductsFilter,
    nutritionalFactFilter,
    minPrice,
    maxPrice,
    nutritionalFactMaxMin,
    filterDrawerOpen,
  ]);

  return (
    <Drawer
      style={{ zIndex: 1600 }}
      disableScrollLock={true}
      anchor={language === "arabic" ? "right" : "left"}
      open={filterDrawerOpen}
      onClose={handleCloseFilterDrawer}
      PaperProps={{
        className: "sf-product-filter",
        style: {
          width: "100%",
          height: "100%",
          backgroundColor: "#fff",
          boxShadow: "0px 0px 0px 0px",
        },
      }}
    >
      <header className="sf-product-filter__header" dir={language === "arabic" ? "rtl" : "ltr"}>
        <h1>{language === "arabic" ? "التصفية والترتيب" : "Filter & Sort"}</h1>
        <button type="button" aria-label={language === "arabic" ? "إغلاق" : "Close filters"} onClick={handleCloseFilterDrawer}><span aria-hidden="true">×</span></button>
      </header>
      {fetchFiltersLoading ? (
        <div className="sf-product-filter__body"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
            padding: "42px 12px 0 12px",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <div className="sf-product-filter__body"
          style={{
            overflowY: "auto",
            paddingTop: "50px",
            height: "calc(100vh - 50px - 50px)",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <ProductFilterList
            sortOption={sortOption}
            setSortOption={setSortOption}
            filtersToApply={filtersToApply}
            setFiltersToApply={setFiltersToApply}
            priceRangeToApply={priceRangeToApply}
            setPriceRangeToApply={setPriceRangeToApply}
            categoriesToApply={categoriesToApply}
            setCategoriesToApply={setCategoriesToApply}
            availableProducts={availableProducts}
            setAvailableProducts={setAvailableProducts}
            nutritionalFact={nutritionalFact}
            setNutritionalFact={setNutritionalFact}
            handleClearFilters={handleClearFilters}
          />
        </div>
      )}
      {applyError && <p className="sf-product-filter__error" role="alert">{language === "arabic" ? "تعذر تحميل النتائج. حاول مرة أخرى." : "Couldn’t load results. Please try again."}</p>}
      <div className="sf-product-filter__footer"
        style={{
          position: "absolute",
          bottom: 0,
          width: "100%",
          backgroundColor: "#fff",
          boxShadow: "0 -0.125rem 0.625rem 0 rgb(0 0 0 / 10%)",
          display: "flex",
          justifyContent: "center",
          paddingBottom: 8,
          marginBottom: 0,
          height: 60,
          zIndex: 4,
          padding: 7,
        }}
      >
        <Button
          style={{
            width: "97%",
            fontSize: 12,
            height: "100%",
            boxShadow: "none",
            textTransform: "none",
            backgroundColor: handleButtonDisable()
              ? "rgb(156, 156, 156)"
              : themeColor,
            color: "#fff",
            fontWeight: 400,
          }}
          color={"primary"}
          disabled={applying || fetchFiltersLoading || handleButtonDisable()}
          variant="contained"
          onClick={handleFilter}
        >
          {applying ? (language === "arabic" ? "جاري التحميل…" : "Loading…") : (language === "arabic" ? "عرض النتائج" : "Show results")}
        </Button>
      </div>
    </Drawer>
  );
};

export default ProductFilter;
